import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, Pill } from "@/components/Brand";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Save,
} from "lucide-react";

const QTYPES = [
  { v: "short_text", l: "Short text" },
  { v: "long_text", l: "Long text" },
  { v: "number", l: "Number" },
  { v: "date", l: "Date" },
  { v: "multiple_choice", l: "Multiple choice" },
  { v: "checkboxes", l: "Checkboxes" },
  { v: "dropdown", l: "Dropdown" },
  { v: "rating", l: "Rating 1–5" },
  { v: "file", l: "File / Photo" },
];

export default function FormBuilder() {
  const [tpl, setTpl] = useState(null);

  useEffect(() => {
    api.get("/form-template").then((r) => setTpl(r.data));
  }, []);

  if (!tpl) return <div className="text-slate-500 text-sm">Loading form…</div>;

  const update = (sections) => setTpl({ ...tpl, sections });

  const addSection = () => {
    update([
      ...tpl.sections,
      { id: `s${Date.now()}`, title: "New section", questions: [] },
    ]);
  };

  const moveSection = (i, dir) => {
    const arr = [...tpl.sections];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    update(arr);
  };

  const updateSection = (i, patch) => {
    const arr = [...tpl.sections];
    arr[i] = { ...arr[i], ...patch };
    update(arr);
  };

  const removeSection = (i) => {
    if (!window.confirm("Delete section?")) return;
    update(tpl.sections.filter((_, idx) => idx !== i));
  };

  const addQuestion = (si) => {
    const arr = [...tpl.sections];
    arr[si].questions.push({
      id: `q${Date.now()}`,
      type: "short_text",
      label: "New question",
      required: false,
      options: [],
    });
    update(arr);
  };

  const updateQuestion = (si, qi, patch) => {
    const arr = [...tpl.sections];
    arr[si].questions[qi] = { ...arr[si].questions[qi], ...patch };
    update(arr);
  };

  const moveQuestion = (si, qi, dir) => {
    const arr = [...tpl.sections];
    const qs = [...arr[si].questions];
    const j = qi + dir;
    if (j < 0 || j >= qs.length) return;
    [qs[qi], qs[j]] = [qs[j], qs[qi]];
    arr[si].questions = qs;
    update(arr);
  };

  const removeQuestion = (si, qi) => {
    const arr = [...tpl.sections];
    arr[si].questions = arr[si].questions.filter((_, idx) => idx !== qi);
    update(arr);
  };

  const save = async () => {
    try {
      await api.put("/admin/form-template", {
        sections: tpl.sections,
        name: tpl.name,
        description: tpl.description,
      });
      toast.success("Form template saved. Surveyors see updates instantly.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Form Builder"
        title="Survey form (live for surveyors)"
        action={
          <button
            onClick={save}
            data-testid="form-builder-save-btn"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            <Save size={16} /> Save form
          </button>
        }
      />

      <div className="wi-card p-4 mb-6 flex flex-wrap items-center gap-3">
        <span className="data-label">Form</span>
        <input
          value={tpl.name}
          onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
          className="flex-1 min-w-[200px] border border-slate-200 rounded-md px-3 py-1.5 text-sm font-medium"
          data-testid="form-name-input"
        />
        <Pill tone="green">{tpl.sections.length} sections</Pill>
        <Pill tone="blue">
          {tpl.sections.reduce((a, s) => a + s.questions.length, 0)} questions
        </Pill>
      </div>

      <div className="space-y-4">
        {tpl.sections.map((sec, si) => (
          <div key={sec.id} className="wi-card p-5" data-testid={`section-${si}`}>
            <div className="flex items-center gap-2 mb-4">
              <GripVertical size={16} className="text-slate-400" />
              <input
                value={sec.title}
                onChange={(e) => updateSection(si, { title: e.target.value })}
                className="flex-1 text-lg font-semibold text-slate-900 border-0 focus:outline-none focus:ring-2 focus:ring-blue-600 rounded px-1"
                data-testid={`section-title-${si}`}
              />
              <div className="flex items-center gap-1">
                <button onClick={() => moveSection(si, -1)} className="p-1.5 hover:bg-slate-100 rounded"><ArrowUp size={14} /></button>
                <button onClick={() => moveSection(si, 1)} className="p-1.5 hover:bg-slate-100 rounded"><ArrowDown size={14} /></button>
                <button onClick={() => removeSection(si)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="space-y-3">
              {sec.questions.map((q, qi) => (
                <div key={q.id} className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid={`question-${si}-${qi}`}>
                  <div className="flex gap-2 items-center mb-2">
                    <input
                      value={q.label}
                      onChange={(e) => updateQuestion(si, qi, { label: e.target.value })}
                      className="flex-1 text-sm font-medium border border-slate-200 rounded px-2 py-1 bg-white"
                    />
                    <select
                      value={q.type}
                      onChange={(e) => updateQuestion(si, qi, { type: e.target.value })}
                      className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                    >
                      {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <label className="text-xs flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={!!q.required}
                        onChange={(e) => updateQuestion(si, qi, { required: e.target.checked })}
                      /> Required
                    </label>
                    <button onClick={() => moveQuestion(si, qi, -1)} className="p-1 hover:bg-slate-200 rounded"><ArrowUp size={12} /></button>
                    <button onClick={() => moveQuestion(si, qi, 1)} className="p-1 hover:bg-slate-200 rounded"><ArrowDown size={12} /></button>
                    <button onClick={() => removeQuestion(si, qi)} className="p-1 hover:bg-red-100 text-red-600 rounded"><Trash2 size={12} /></button>
                  </div>
                  {["multiple_choice", "checkboxes", "dropdown"].includes(q.type) && (
                    <div>
                      <input
                        placeholder="Comma-separated options"
                        value={(q.options || []).join(", ")}
                        onChange={(e) => updateQuestion(si, qi, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        className="w-full text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                      />
                    </div>
                  )}
                </div>
              ))}
              <button
                onClick={() => addQuestion(si)}
                data-testid={`add-question-${si}`}
                className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-800 font-medium"
              >
                <Plus size={12} /> Add question
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addSection}
        data-testid="add-section-btn"
        className="mt-4 w-full border border-dashed border-slate-300 rounded-md py-3 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center gap-2"
      >
        <Plus size={14} /> Add section
      </button>
    </div>
  );
}
